import type CrawlerElement from '../crawler/crawler.element'
import type { InlineGridRenderer } from '../crawler/crawler.element'
import type { Params } from '../params/params.types'
import type InlineGridProtocolController from './inline-grid-protocol'
import {
  getIframeBaseUri,
  getIframeRouteReason,
  getSdkScriptOrigin,
  type IframeRouteReason,
  type SdkScriptOrigin,
} from '../util/script-params'

const FORWARDED_PARAM_KEYS = new Set([
  'amount',
  'c_src',
  'c_src2',
  'currency',
  'designation',
  'frequency',
  'preset1',
  'preset2',
  'preset3',
  'preset4',
  'recurring',
  'recurringEndDate',
])

const INLINE_GRID_IFRAME_URL_BYTE_LIMIT = 8 * 1024
// Wait for Whammy's measured content height instead of reserving empty space.
const INLINE_GRID_INITIAL_HEIGHT = '0px'
const INLINE_GRID_BORDER_RADIUS = '16px'
// The grid's own shadow (campaign-page-blocks' Donation block) paints outside its form, and the
// form fills the iframe, so the iframe clips it. The host page draws the same shadow instead.
const INLINE_GRID_DROP_SHADOW = '0 8px 16px rgb(0 0 0 / 10%)'

interface BuildInlineGridIframeUrlProps {
  baseUri: string
  campaignId: string
  componentId: string
  hostSearch?: string
  inlineGridProfile?: unknown
  params: Params | Record<string, unknown>
  pgdid?: string
  sdkBuild?: string
  sdkOrigin?: SdkScriptOrigin
  sdkRoute?: IframeRouteReason
  sdkInstanceId: string
}

interface InlineGridIframeRendererDependencies {
  fallback: InlineGridRenderer
  getBaseUri?: () => string
  getSdkBuild?: () => string
  getSdkOrigin?: () => SdkScriptOrigin
  getSdkRoute?: () => IframeRouteReason
  protocol: InlineGridProtocolController
}

const appendString = (searchParams: URLSearchParams, key: string, value: unknown): void => {
  if (typeof value === 'string' && value.length > 0) {
    searchParams.set(key, value)
  }
}

const getUtf8ByteLength = (value: string): number => new Blob([value]).size

export const buildInlineGridIframeUrl = ({
  baseUri,
  campaignId,
  componentId,
  hostSearch = '',
  inlineGridProfile,
  params,
  pgdid,
  sdkBuild = process.env.SDK_BUILD_ID || 'unknown',
  sdkOrigin = getSdkScriptOrigin(),
  sdkRoute = getIframeRouteReason(),
  sdkInstanceId,
}: BuildInlineGridIframeUrlProps): string => {
  const url = new URL(baseUri)
  const basePath = url.pathname.replace(/\/$/, '')

  // The grid route exists only beside Whammy's canonical /donate/embedded app.
  // A legacy /embedded override must fail closed through the Web Component.
  if (!basePath.endsWith('/donate/embedded')) {
    throw new Error('Inline grid requires the canonical Whammy embedded route')
  }

  url.pathname = `${basePath}/grid`
  url.hash = ''
  url.search = ''

  url.searchParams.set('cid', campaignId)
  url.searchParams.set('component_id', componentId)
  appendString(url.searchParams, 'pgdid', pgdid)

  if (inlineGridProfile === undefined || inlineGridProfile === null) {
    throw new Error('Inline grid requires a profile')
  }

  const serializedProfile = JSON.stringify(inlineGridProfile)

  if (!serializedProfile) {
    throw new Error('Inline grid profile could not be serialized')
  }

  url.searchParams.set('grid_profile', serializedProfile)

  Object.entries(params).forEach(([key, value]) => {
    if (FORWARDED_PARAM_KEYS.has(key) || key.startsWith('utm_')) {
      appendString(url.searchParams, key, value)
    }
  })

  // Non-production Optimizely overrides are parsed and rejected by Whammy in production.
  new URLSearchParams(hostSearch).forEach((value, key) => {
    if (key.startsWith('ff_')) {
      appendString(url.searchParams, key, value)
    }
  })

  url.searchParams.set('sdk_build', sdkBuild)
  url.searchParams.set('sdk_origin', sdkOrigin)
  url.searchParams.set('sdk_route', sdkRoute)
  url.searchParams.set('sdk_surface', 'inline-grid')
  url.searchParams.set('sdk_instance_id', sdkInstanceId)

  const iframeUrl = url.toString()

  if (getUtf8ByteLength(iframeUrl) > INLINE_GRID_IFRAME_URL_BYTE_LIMIT) {
    throw new Error('Inline grid iframe URL exceeds the 8 KiB limit')
  }

  return iframeUrl
}

export const makeInlineGridIframeRenderer = ({
  fallback,
  getBaseUri = getIframeBaseUri,
  getSdkBuild = () => process.env.SDK_BUILD_ID || 'unknown',
  getSdkOrigin = getSdkScriptOrigin,
  getSdkRoute = getIframeRouteReason,
  protocol,
}: InlineGridIframeRendererDependencies): InlineGridRenderer => {
  return (element: CrawlerElement): void => {
    let cleanupProtocol: ((failureReason?: 'grid_error') => void) | undefined

    try {
      const parent = element.el.parentNode

      if (!parent) {
        throw new Error('Inline grid placeholder is detached')
      }

      const instanceId = protocol.createInstanceId(element.campaignId, element.el.id)
      const src = buildInlineGridIframeUrl({
        baseUri: getBaseUri(),
        campaignId: element.campaignId,
        componentId: element.el.id,
        hostSearch: element.win.location.search,
        inlineGridProfile: element.inlineGridProfile,
        params: element.modalSDK.getParams(),
        pgdid: element.pgdid,
        sdkBuild: getSdkBuild(),
        sdkOrigin: getSdkOrigin(),
        sdkRoute: getSdkRoute(),
        sdkInstanceId: instanceId,
      })
      const iframe = element.win.document.createElement('iframe')

      iframe.id = element.el.id
      iframe.className = element.el.className
      // The inline grid is primary, above-the-fold content. Loading it eagerly avoids
      // adding browser lazy-load latency on top of Whammy's server-side config work.
      iframe.setAttribute('loading', 'eager')
      iframe.name = `eg-inline-grid-iframe--${element.campaignId}--${element.el.id}`
      iframe.referrerPolicy = 'strict-origin-when-cross-origin'
      iframe.src = src
      iframe.title = element.organizationName ? `Donate to ${element.organizationName}` : 'Donate'
      iframe.style.border = '0'
      iframe.style.borderRadius = INLINE_GRID_BORDER_RADIUS
      iframe.style.display = 'block'
      iframe.style.width = '100%'
      iframe.style.minWidth = '345px'
      iframe.style.height = INLINE_GRID_INITIAL_HEIGHT

      const scrollContainer = element.win.document.createElement('div')
      scrollContainer.style.width = '100%'
      scrollContainer.style.minWidth = '0'
      scrollContainer.style.overflowX = 'auto'
      scrollContainer.tabIndex = 0
      scrollContainer.setAttribute('role', 'region')
      scrollContainer.setAttribute('aria-label', iframe.title)
      // On the container, not the iframe: its overflow-x would clip a shadow drawn on the iframe.
      // Until Whammy reports a height the container is 0px tall, so no shadow shows early.
      if (element.config?.props?.dropShadow === true) {
        scrollContainer.style.borderRadius = INLINE_GRID_BORDER_RADIUS
        scrollContainer.style.boxShadow = INLINE_GRID_DROP_SHADOW
      }
      scrollContainer.appendChild(iframe)

      let didFallback = false
      // Recovery is scoped to this element; it does not change the initial page-wide decision or
      // the renderer already selected for sibling grids.
      const fallbackToLegacy = (): void => {
        if (didFallback) {
          return
        }

        didFallback = true
        cleanupProtocol?.()
        if (element.restoreReplacement()) {
          console.warn('[SDK] Standalone inline grid unavailable; using legacy renderer')
          fallback(element)
        }
      }

      // Listen before insertion so a fast iframe load cannot outrun the handshake listener.
      cleanupProtocol = protocol.register({
        campaignId: element.campaignId,
        componentId: element.el.id,
        iframe,
        instanceId,
        onUnavailable: fallbackToLegacy,
        targetOrigin: new URL(src).origin,
      })
      parent.replaceChild(scrollContainer, element.el)
      // Chromium skips layout in off-screen `content-visibility: auto` sections, which leaves the
      // cross-site grid unsized until the watchdog expires. Reading the box lays the iframe out.
      iframe.getBoundingClientRect()
      element.setReplacement(scrollContainer)
      element.addCleanup(cleanupProtocol)
    } catch (error) {
      cleanupProtocol?.('grid_error')
      console.error('[SDK] Failed to render the standalone inline grid; using legacy:', error)
      fallback(element)
    }
  }
}
